import {
  AppError,
  ErrorHandler,
  ErrorHandlingResult,
  ErrorCategory,
  NetworkError,
  AuthenticationError,
  AuthorizationError,
  ValidationError,
  StorageError,
  DatabaseError,
  BusinessLogicError,
  ExternalApiError
} from '../types/error';
import { ErrorLogger } from './errorLogger';

/**
 * Network error handler with retry logic
 */
export class NetworkErrorHandler implements ErrorHandler {
  private maxRetries: number = 3;
  private retryDelay: number = 1000;

  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.NETWORK;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const networkError = error as NetworkError;

    await ErrorLogger.logError(error);

    // Handle timeout errors
    if (networkError.timeout) {
      return {
        success: false,
        message: 'The request timed out. Please check your internet connection and try again.',
        shouldRetry: true,
        retryAfter: this.retryDelay * 2,
        recoveryActions: [
          {
            type: 'retry',
            label: 'Retry',
            description: 'Try the request again',
            icon: 'bi-arrow-clockwise'
          },
          {
            type: 'manual_action',
            label: 'Check Connection',
            description: 'Verify your internet connection',
            icon: 'bi-wifi'
          }
        ]
      };
    }

    // Handle specific status codes
    if (networkError.statusCode) {
      switch (Math.floor(networkError.statusCode / 100)) {
        case 4: // 4xx client errors
          return {
            success: false,
            message: networkError.userMessage,
            shouldRetry: false,
            recoveryActions: networkError.recoveryActions
          };

        case 5: // 5xx server errors
          return {
            success: false,
            message: 'Server error occurred. Please try again in a few moments.',
            shouldRetry: true,
            retryAfter: this.retryDelay,
            recoveryActions: [
              {
                type: 'retry',
                label: 'Retry',
                description: 'Try again in a few moments',
                icon: 'bi-arrow-clockwise'
              },
              {
                type: 'contact_support',
                label: 'Contact Support',
                description: 'If the problem persists, contact support',
                icon: 'bi-envelope'
              }
            ]
          };
      }
    }

    // Default network error handling
    return {
      success: false,
      message: networkError.userMessage,
      shouldRetry: true,
      retryAfter: this.retryDelay,
      recoveryActions: networkError.recoveryActions
    };
  }
}

/**
 * Authentication error handler
 */
export class AuthenticationErrorHandler implements ErrorHandler {
  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.AUTHENTICATION;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const authError = error as AuthenticationError;

    await ErrorLogger.logError(error);

    // Handle expired sessions or JWT errors
    if (authError.expired || error.message?.includes('JWT') || error.message?.includes('exp')) {
      try {
        // Try to refresh the session
        const refreshResult = await this.attemptSessionRefresh();
        if (refreshResult.success) {
          return {
            success: true,
            message: 'Session refreshed successfully',
            shouldRetry: true,
            retryAfter: 100
          };
        }
      } catch (refreshError) {
        console.warn('Failed to refresh session:', refreshError);
      }

      // If refresh failed, redirect to login
      return {
        success: false,
        message: 'Your session has expired. Please sign in again.',
        redirectUrl: '/login',
        recoveryActions: [
          {
            type: 'manual_action',
            label: 'Sign In',
            description: 'Sign in to continue',
            icon: 'bi-box-arrow-in-right'
          }
        ]
      };
    }

    // Redirect to login
    return {
      success: false,
      message: authError.userMessage,
      redirectUrl: '/login',
      recoveryActions: authError.recoveryActions
    };
  }

  private async attemptSessionRefresh(): Promise<{ success: boolean }> {
    try {
      // Import supabase client dynamically to avoid circular dependencies
      const { supabase } = await import('../services/supabaseClient');
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        throw error;
      }

      return { success: !!data.session };
    } catch (error) {
      console.error('Session refresh failed:', error);
      return { success: false };
    }
  }
}

/**
 * Authorization error handler
 */
export class AuthorizationErrorHandler implements ErrorHandler {
  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.AUTHORIZATION;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const authzError = error as AuthorizationError;

    await ErrorLogger.logError(error);

    return {
      success: false,
      message: authzError.userMessage,
      shouldRetry: false,
      recoveryActions: authzError.recoveryActions
    };
  }
}

/**
 * Validation error handler
 */
export class ValidationErrorHandler implements ErrorHandler {
  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.VALIDATION;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const validationError = error as ValidationError;

    // Don't log validation errors as they are user input issues

    return {
      success: false,
      message: validationError.userMessage,
      shouldRetry: false,
      recoveryActions: validationError.recoveryActions
    };
  }
}

/**
 * Storage error handler
 */
export class StorageErrorHandler implements ErrorHandler {
  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.STORAGE;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const storageError = error as StorageError;

    await ErrorLogger.logError(error);

    // Handle file size errors
    if (storageError.fileSize && storageError.fileSize > 5 * 1024 * 1024) {
      return {
        success: false,
        message: 'File is too large. Please use a file smaller than 5MB.',
        shouldRetry: false,
        recoveryActions: [
          {
            type: 'manual_action',
            label: 'Reduce File Size',
            description: 'Compress or resize your file',
            icon: 'bi-file-earmark-arrow-down'
          }
        ]
      };
    }

    return {
      success: false,
      message: storageError.userMessage,
      shouldRetry: true,
      retryAfter: 2000,
      recoveryActions: storageError.recoveryActions
    };
  }
}

/**
 * Database error handler
 */
export class DatabaseErrorHandler implements ErrorHandler {
  private retryableOperations = ['select', 'insert', 'update', 'delete'];

  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.DATABASE;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const dbError = error as DatabaseError;

    await ErrorLogger.logError(error);

    // Handle constraint violations
    if (dbError.constraint === 'unique') {
      return {
        success: false,
        message: 'This item already exists. Please try with different values.',
        shouldRetry: false,
        recoveryActions: [
          {
            type: 'manual_action',
            label: 'Modify Data',
            description: 'Change your input to make it unique',
            icon: 'bi-pencil'
          }
        ]
      };
    }

    if (dbError.constraint === 'foreign_key') {
      return {
        success: false,
        message: 'Referenced data is missing. Please refresh and try again.',
        shouldRetry: false,
        recoveryActions: [
          {
            type: 'refresh',
            label: 'Refresh Page',
            description: 'Refresh the page to get updated data',
            icon: 'bi-arrow-clockwise'
          }
        ]
      };
    }

    // Check if operation is retryable
    const isRetryable = dbError.operation
      ? this.retryableOperations.includes(dbError.operation)
      : true;

    return {
      success: false,
      message: dbError.userMessage,
      shouldRetry: isRetryable,
      retryAfter: 1500,
      recoveryActions: dbError.recoveryActions
    };
  }
}

/**
 * Business logic error handler
 */
export class BusinessLogicErrorHandler implements ErrorHandler {
  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.BUSINESS_LOGIC;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const businessError = error as BusinessLogicError;

    await ErrorLogger.logError(error, {
      businessRule: businessError.businessRule,
      currentState: businessError.currentState,
      expectedState: businessError.expectedState
    });

    return {
      success: false,
      message: businessError.userMessage,
      shouldRetry: false,
      recoveryActions: businessError.recoveryActions
    };
  }
}

/**
 * External API error handler
 */
export class ExternalApiErrorHandler implements ErrorHandler {
  private retryDelay = 2000;

  canHandle(error: AppError): boolean {
    return error.category === ErrorCategory.EXTERNAL_API;
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    const apiError = error as ExternalApiError;

    await ErrorLogger.logError(error);

    // Handle rate limiting
    if (apiError.statusCode === 429) {
      return {
        success: false,
        message: 'Too many requests. Please wait a moment and try again.',
        shouldRetry: true,
        retryAfter: this.retryDelay * 2,
        recoveryActions: [
          {
            type: 'retry',
            label: 'Retry Later',
            description: 'Wait a moment and try again',
            icon: 'bi-clock'
          }
        ]
      };
    }

    // Handle server errors from external APIs
    if (apiError.statusCode && apiError.statusCode >= 500) {
      return {
        success: false,
        message: `${apiError.apiName || 'External service'} is currently unavailable. Please try again later.`,
        shouldRetry: true,
        retryAfter: this.retryDelay,
        recoveryActions: [
          {
            type: 'retry',
            label: 'Retry',
            description: 'Try again in a few moments',
            icon: 'bi-arrow-clockwise'
          },
          {
            type: 'contact_support',
            label: 'Contact Support',
            description: 'Contact support if the problem persists',
            icon: 'bi-envelope'
          }
        ]
      };
    }

    return {
      success: false,
      message: apiError.userMessage,
      shouldRetry: false,
      recoveryActions: apiError.recoveryActions
    };
  }
}

/**
 * Default error handler for unknown or unhandled errors
 */
export class DefaultErrorHandler implements ErrorHandler {
  canHandle(error: AppError): boolean {
    return true; // Can handle any error as fallback
  }

  async handle(error: AppError): Promise<ErrorHandlingResult> {
    await ErrorLogger.logError(error);

    return {
      success: false,
      message: error.userMessage || 'An unexpected error occurred. Please try again.',
      shouldRetry: error.retryable,
      retryAfter: 1000,
      recoveryActions: error.recoveryActions || [
        {
          type: 'retry',
          label: 'Try Again',
          description: 'Retry the operation',
          icon: 'bi-arrow-clockwise'
        },
        {
          type: 'contact_support',
          label: 'Contact Support',
          description: 'Contact support if the problem persists',
          icon: 'bi-envelope'
        }
      ]
    };
  }
}
